import type { createSearchkitClient } from "@ui/lib/searchkit";
import type { InstantSearchProps, UseInfiniteHitsProps } from "react-instantsearch";

// instantsearch.js is only a transitive dependency — derive its types from
// the react-instantsearch surface instead of deep-importing them.
type InfiniteHitsCache = NonNullable<UseInfiniteHitsProps["cache"]>;
type UiState = NonNullable<InstantSearchProps["initialUiState"]>;

export type SearchSessionKey = "destinations" | "marketplace" | "members" | "programs";

export type SearchRequestBody = Record<string, unknown>;
export type SearchRequestEnhancer = (body: SearchRequestBody) => SearchRequestBody;

/**
 * Mutable indirection between a session-cached transporter and the currently
 * mounted page: the transporter delegates here, and each mount re-points the
 * fields at its own state setters and request enhancer.
 */
export interface SearchTransportCallbackTarget {
  onSuccess: () => void;
  onError: () => void;
  getEnhancer: () => SearchRequestEnhancer | undefined;
  /** Identifies the mount currently wired in, so its unmount cleanup doesn't
   *  detach a successor's callbacks. */
  owner?: unknown;
}

export interface SearchSessionClient {
  searchClient: ReturnType<typeof createSearchkitClient>;
  callbackTarget: SearchTransportCallbackTarget;
}

export interface SearchResultsSnapshot {
  hasMore: boolean;
  hits: unknown[];
  totalCount: number;
}

export interface SessionHitsCache extends InfiniteHitsCache {
  clear: () => void;
}

interface SearchSession {
  client?: SearchSessionClient;
  uiState?: UiState;
  hitsCache?: SessionHitsCache;
  resultsSnapshot?: SearchResultsSnapshot;
}

type CacheReadArgs = Parameters<InfiniteHitsCache["read"]>[0];
type CacheWriteArgs = Parameters<InfiniteHitsCache["write"]>[0];

// Key-order-independent serialisation so two equal states always produce the
// same cache key regardless of property insertion order.
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);
    return `{${entries.join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

// Mirrors instantsearch's session-storage cache: hits are valid for the exact
// search state minus the page (each page is a separate entry in `hits`).
function stateKey(state: CacheReadArgs["state"]): string {
  const { page: _page, ...rest } = state ?? {};
  return stableStringify(rest);
}

function createSessionHitsCache(): SessionHitsCache {
  let cached: { key: string; hits: CacheWriteArgs["hits"] } | null = null;

  return {
    read({ state }: CacheReadArgs) {
      return cached && cached.key === stateKey(state) ? cached.hits : null;
    },
    write({ state, hits }: CacheWriteArgs) {
      cached = { key: stateKey(state), hits };
    },
    clear() {
      cached = null;
    },
  };
}

const sessions = new Map<SearchSessionKey, SearchSession>();

/**
 * Module-scoped search state that survives Inertia navigations (page
 * components unmount on every visit), so revisiting a search page restores
 * its client, refinements, and hits instead of re-running from a skeleton.
 * The SSR bundle shares this module graph: server-side callers get a
 * throwaway session so nothing leaks between rendered requests.
 */
export function getSearchSession(key: SearchSessionKey): SearchSession {
  if (typeof window === "undefined") return {};

  let session = sessions.get(key);
  if (!session) {
    session = {};
    sessions.set(key, session);
  }
  return session;
}

/** Lazily creates the session's hits cache so it can be threaded into `useInfiniteHits`. */
export function getSessionHitsCache(key: SearchSessionKey): SessionHitsCache {
  const session = getSearchSession(key);
  session.hitsCache ??= createSessionHitsCache();
  return session.hitsCache;
}

/**
 * Persists uiState minus per-index keys that must not survive a remount:
 * `page` always — restoring the infinite-hits page against a flushed cache
 * would resume mid-list with earlier pages unreachable, which is also why
 * `page` never rides the URL grammar — plus any caller keys whose widgets
 * are conditionally mounted (their restored state would be silently dropped
 * and diverge from the cached hits).
 */
export function rememberSearchUiState(key: SearchSessionKey, uiState: UiState, omit: string[] = []): void {
  if (typeof window === "undefined") return;

  const dropped = new Set(["page", ...omit]);
  getSearchSession(key).uiState = Object.fromEntries(
    Object.entries(uiState).map(([index, indexState]) => [
      index,
      Object.fromEntries(Object.entries(indexState ?? {}).filter(([stateKey]) => !dropped.has(stateKey))),
    ]),
  ) as UiState;
}

/**
 * Drops one session's cached hits and result snapshot — use when the next
 * search is known to differ from the cached one (e.g. an explicit URL query
 * overrides the restored uiState).
 */
export function flushSearchSession(key: SearchSessionKey): void {
  const session = sessions.get(key);
  if (!session) return;

  session.hitsCache?.clear();
  session.resultsSnapshot = undefined;
}

/**
 * Drops cached hits and result snapshots — mutations may have changed what a
 * search returns. Clients and uiState survive: they're expensive to rebuild
 * and a stale refinement is the user's own last input, not stale data.
 */
export function flushSearchSessions(): void {
  for (const key of sessions.keys()) {
    flushSearchSession(key);
  }
}
